import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ComponentType,
  type ReactNode,
} from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  ArrowRight,
  CalendarDays,
  CalendarPlus,
  Camera,
  Check,
  ChevronLeft,
  Clock3,
  FileBadge2,
  MapPin,
  MoreVertical,
  Pencil,
  Shield,
  Truck,
  Power,
  Trash2,
  type LucideProps,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Permit } from '@/components/guards/Permit'
import { Skeleton } from '@/components/ui/skeleton'
import { Avatar } from '@/components/ui/avatar'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { CoverageMap, DatePicker } from '@/components/shared'
import { TechnicianReferenceBadge } from '@/components/technicians/TechnicianReferenceBadge'
import {
  JobStatusBadge,
  TechnicianStatusBadge,
} from '@/components/shared/StatusBadges'
import { DataTablePagination } from '@/components/shared/DataTablePagination'
import { DeactivateTechnicianDialog } from '@/components/technicians/DeactivateTechnicianDialog'
import { DeleteTechnicianDialog } from '@/components/technicians/DeleteTechnicianDialog'
import { DocumentsTab } from '@/components/technicians/tabs/DocumentsTab'
import { NotesTab } from '@/components/technicians/tabs/NotesTab'
import { toast } from '@/components/ui/toast'
import {
  TECHNICIAN_DETAIL_DOCUMENTS_QUERY,
  TECHNICIAN_DETAIL_TAB_STALE_TIME,
  technicianDocumentsQueryOptions,
  technicianJobsQueryOptions,
  useTechnician,
  useTechnicianJobs,
} from '@/hooks/useTechnicians'
import { useListParams } from '@/hooks/useListParams'
import { useBreadcrumbLabel } from '@/contexts/BreadcrumbContext'
import {
  SETTINGS_IMAGE_ACCEPT,
  SETTINGS_IMAGE_MAX_BYTES,
  validateSettingsImage,
} from '@/lib/settingsImageUpload'
import { getFileSizeLimitMb } from '@/lib/uploadValidation'
import {
  formatCurrency,
  formatDate,
  formatInteger,
  formatPercent,
} from '@/lib/format'
import {
  DEFAULT_WORKING_DAYS,
  TECHNICIAN_WEEK_DAYS_UI,
  getTechnicianRolePreset,
  fromApiWorkingDays,
  getTechnicianPrimaryStatus,
  getTechnicianSecondaryStatusDetail,
  getCommissionRates,
  parseBreakRange,
  technicianDetailToEditPayload,
} from '@/lib/technicians'
import { getApiErrorMessage } from '@/services/api'
import { techniciansApi } from '@/services/technicians'
import { cn } from '@/lib/utils'
import { formatPhoneNumber } from '@/lib/phone'
import {
  FORM_CONTROL_FOCUS_CLASS,
  FORM_CONTROL_FOCUS_WITHIN_CLASS,
} from '@/components/ui/form-control-styles'
import type { Job, ListQuery } from '@/types'
import certificateIcon from '@/assets/technicians/detail/certificate.svg'
import editIcon from '@/assets/technicians/detail/edit.svg'
import briefcaseIcon from '@/assets/technicians/detail/briefcase.svg'
import phoneActionIcon from '@/assets/technicians/detail/phone-action.svg'
import whatsappIcon from '@/assets/technicians/detail/whatsapp.svg'
import mailActionIcon from '@/assets/technicians/detail/mail-action.svg'
import calendarIcon from '@/assets/technicians/detail/calendar.svg'
import phoneDetailIcon from '@/assets/technicians/detail/phone-detail.svg'
import mailDetailIcon from '@/assets/technicians/detail/mail-detail.svg'
import mapPinIcon from '@/assets/technicians/detail/map-pin.svg'

const TAB_VALUES = ['profile', 'performance', 'documents'] as const
type TechnicianDetailTab = (typeof TAB_VALUES)[number]

const TECHNICIAN_AVATAR_BACKGROUND =
  'linear-gradient(142deg, #FFF1EA -18.72%, #FF5A1F 33.89%, #FF5A1F 109.94%)'

const primaryBtnClass =
  'rounded-xl bg-primary px-3 py-3 text-sm font-medium leading-none text-primary-foreground hover:bg-primary/90'
const tabTriggerClass =
  'inline-flex h-8 items-center justify-center gap-2 rounded-lg border-0 px-3 py-2 text-sm font-medium leading-[1.4] text-[#3d434e] shadow-none data-[state=active]:border-0 data-[state=active]:bg-[#FF5A1F] data-[state=active]:text-white'

const thClass =
  'h-10 border-b border-[#e7e9ef] bg-[#fbfbfc] px-3 py-2 text-[10px] font-medium leading-[14px] tracking-normal text-[#3d434e]'
const tdClass =
  'h-16 border-b border-[#e7e9ef] px-3 py-2 align-middle text-sm leading-[1.4]'
const figmaCardClass =
  'overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_4px_-4px_rgba(38,45,66,0.05),0_8px_16px_-4px_rgba(38,45,66,0.1)]'
const sidebarSectionLabelClass = 'text-xs font-medium leading-4 text-[#6c7285]'
const profileSectionLabelClass =
  'font-heading text-sm font-semibold leading-[1.2] text-[#3d434e]'
const detailDividerClass = 'border-t border-[#e7e9ef]'
const performanceDividerClass = 'border-t border-[#eef0f4]'

const emptyValue = <span>--</span>
const activityCategoryConfigs = [
  { label: 'Cafards', color: '#d4380d' },
  { label: 'Termites & Insectes xylophages', color: '#0d9488' },
  { label: 'Guêpes / Frelons', color: '#a16207' },
  { label: 'Chenilles processionnaires', color: '#65a30d' },
  { label: 'Pigeons', color: '#2e5ce6' },
  { label: 'Désinsectisation', color: '#57534e' },
  { label: 'Dératisation (Rats / Souris)', color: '#b45309' },
  { label: 'Punaises de lit', color: '#c71e00' },
] as const
type ActivityCategoryLabel = (typeof activityCategoryConfigs)[number]['label']

const activityCategoryAliases: Record<string, ActivityCategoryLabel> = {
  'Wasps / Hornets': 'Guêpes / Frelons',
  Cockroaches: 'Cafards',
  'Rodent Control (Rats / Mice)': 'Dératisation (Rats / Souris)',
  'Bed Bugs': 'Punaises de lit',
  'Bed bugs': 'Punaises de lit',
  'Termites & Wood-boring Insects': 'Termites & Insectes xylophages',
  'Processionary Caterpillars': 'Chenilles processionnaires',
  'General Insect Control': 'Désinsectisation',
}

function normalizeActivityCategory(
  activity?: string | null
): ActivityCategoryLabel | null {
  const value = activity?.trim()
  if (!value) return null

  const directMatch = activityCategoryConfigs.find(
    category =>
      category.label.localeCompare(value, undefined, {
        sensitivity: 'accent',
      }) === 0
  )
  if (directMatch) return directMatch.label

  const aliasEntry = Object.entries(activityCategoryAliases).find(
    ([alias]) =>
      alias.localeCompare(value, undefined, { sensitivity: 'accent' }) === 0
  )

  return aliasEntry?.[1] ?? null
}

function ZoneTag({ label }: { label: string }) {
  return (
    <span className='inline-flex items-center rounded-xl bg-[#dff8e8] px-3 py-1.5 text-xs font-medium leading-4 text-[#0b6b57]'>
      {label}
    </span>
  )
}

function QuickActionButton({
  label,
  iconSrc,
  tone,
  disabled,
  onClick,
  className,
}: {
  label: string
  iconSrc: string
  tone: 'orange' | 'green' | 'neutral'
  disabled?: boolean
  onClick: () => void
  className?: string
}) {
  const toneClass =
    tone === 'orange'
      ? 'border-[#ff9d75] bg-[#fff1ea] text-[#ff5a1f] hover:bg-[#ffe6db]'
      : tone === 'green'
        ? 'border-[#059669] bg-[#ecfdf5] text-[#059669] hover:bg-[#dcf7e8]'
        : 'border-[#e7e9ef] bg-[#f4f5f8] text-[#6c7285] hover:bg-[#eceff5]'

  return (
    <Button
      type='button'
      variant='outline'
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'h-10 min-w-0 gap-2 rounded-xl border px-3 text-sm font-medium leading-none shadow-none',
        toneClass,
        className
      )}
    >
      <img src={iconSrc} alt='' className='size-4 shrink-0' />
      <span>{label}</span>
    </Button>
  )
}

function SidebarMetaSection({
  title,
  titleClassName,
  children,
}: {
  title: string
  titleClassName?: string
  children: ReactNode
}) {
  return (
    <div className='space-y-2'>
      <p className={cn(sidebarSectionLabelClass, titleClassName)}>{title}</p>
      {children}
    </div>
  )
}

function SummaryFigure({
  label,
  value,
  tone,
}: {
  label: string
  value: ReactNode
  tone: 'blue' | 'green' | 'orange'
}) {
  const toneClass =
    tone === 'blue'
      ? 'bg-[#ebf1fe] text-[#2550cc]'
      : tone === 'green'
        ? 'bg-[#ecfdf5] text-[#047857]'
        : 'bg-[#fff1ea] text-[#ff5a1f]'

  return (
    <div
      className={cn(
        'flex min-h-[52px] min-w-0 flex-col items-center justify-center rounded-xl px-2 py-2 text-center',
        toneClass
      )}
    >
      <span className='w-full truncate text-sm font-medium leading-5'>
        {value}
      </span>
      <span className='mt-0.5 w-full text-[10px] font-medium leading-[14px] uppercase'>
        {label}
      </span>
    </div>
  )
}

function PerformanceMetricCard({
  label,
  value,
  tone,
}: {
  label: string
  value: ReactNode
  tone: 'blue' | 'green' | 'orange'
}) {
  const toneClasses =
    tone === 'blue'
      ? {
          card: 'bg-[#ebf1fe]',
          value: 'text-[#2550cc]',
          label: 'text-[#2e5ce6]',
        }
      : tone === 'green'
        ? {
            card: 'bg-[#ecfdf5]',
            value: 'text-[#047857]',
            label: 'text-[#059669]',
          }
        : {
            card: 'bg-[#fff1ea]',
            value: 'text-[#ff5a1f]',
            label: 'text-[#ff8656]',
          }

  return (
    <div
      className={cn(
        'flex h-[74px] flex-col rounded-xl px-4 py-4 text-center',
        toneClasses.card
      )}
    >
      <span
        className={cn(
          'font-heading text-[20px] font-bold leading-[1.2]',
          toneClasses.value
        )}
      >
        {value}
      </span>
      <span
        className={cn(
          'mt-0.5 text-xs font-medium leading-4',
          toneClasses.label
        )}
      >
        {label}
      </span>
    </div>
  )
}

function PerformanceSectionTitle({ title }: { title: string }) {
  return (
    <div className='flex items-center gap-4'>
      <span className='h-5 w-1 shrink-0 rounded-full bg-[#ff5a1f]' />
      <h3 className='font-heading text-sm font-semibold leading-[1.2] text-[#ff5a1f]'>
        {title}
      </h3>
    </div>
  )
}

function PerformanceSubsectionHeader({
  title,
  icon: Icon,
}: {
  title: string
  icon: ComponentType<LucideProps>
}) {
  return (
    <div className='flex items-center gap-3'>
      <span className='flex size-8 items-center justify-center rounded-lg bg-[#fff1ea] text-[#ff5a1f]'>
        <Icon className='size-4' />
      </span>
      <h3 className='font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
        {title}
      </h3>
    </div>
  )
}

function PerformanceHeaderIcon({ className, ...props }: LucideProps) {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      className={className}
      {...props}
    >
      <path d='M18 20V10M12 20V4M6 20v-6' />
    </svg>
  )
}

function ContactDetailItem({
  iconSrc,
  iconClassName,
  title,
  subtitle,
  onClick,
}: {
  iconSrc: string
  iconClassName?: string
  title: ReactNode
  subtitle?: ReactNode
  onClick?: () => void
}) {
  const content = (
    <div
      className={cn(
        'flex items-center gap-3 rounded-xl text-left',
        onClick && 'transition hover:bg-[#f8fafd]'
      )}
    >
      <div className='flex size-10 shrink-0 items-center justify-center rounded-xl border border-[#e7e9ef] bg-[#f4f5f8] text-[#272c36]'>
        <img
          src={iconSrc}
          alt=''
          className={cn('size-4 shrink-0', iconClassName)}
        />
      </div>
      <div className='min-w-0 flex-1'>
        <p className='break-words text-sm font-medium leading-[1.4] text-[#171a20]'>
          {title}
        </p>
        {subtitle ? (
          <p className='mt-0.5 break-words text-xs font-normal leading-4 text-[#3d434e]'>
            {subtitle}
          </p>
        ) : null}
      </div>
    </div>
  )

  if (!onClick) return content

  return (
    <button
      type='button'
      onClick={onClick}
      className='w-full rounded-xl text-left'
    >
      {content}
    </button>
  )
}

function FigmaCardHeader({
  title,
  subtitle,
  icon: Icon,
  tone = 'orange',
  titleClassName,
}: {
  title: string
  subtitle?: string
  icon: ComponentType<LucideProps>
  tone?: 'orange' | 'green' | 'emerald' | 'amber'
  titleClassName?: string
}) {
  const toneClass =
    tone === 'green'
      ? 'bg-[#eefbf4] text-[#16a34a]'
      : tone === 'emerald'
        ? 'bg-[#ecfdf5] text-[#10b981]'
        : tone === 'amber'
          ? 'bg-[#fbf3d9] text-[#a16207]'
          : 'bg-[#fff1ea] text-[#ff5a1f]'

  return (
    <div className='flex items-center gap-3 px-6 pb-4 pt-6'>
      <div
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-lg',
          toneClass
        )}
      >
        <Icon className='size-4' />
      </div>
      <div className='min-w-0'>
        <h3
          className={cn(
            'font-heading text-base font-semibold leading-[1.2] text-[#171a20]',
            titleClassName
          )}
        >
          {title}
        </h3>
        {subtitle ? (
          <p className='mt-1 text-xs font-normal leading-4 text-[#6c7285]'>
            {subtitle}
          </p>
        ) : null}
      </div>
    </div>
  )
}

function itemLabel(item: string | { name?: string; plate?: string }): string {
  if (typeof item === 'string') return item
  const name = item.name ?? ''
  return item.plate ? `${name} (${item.plate})` : name
}

function itemName(item: string | { name?: string; plate?: string }): string {
  if (typeof item === 'string') return item
  return item.name?.trim() ?? ''
}

function displayValue(value?: string | number | null) {
  if (value == null) return emptyValue
  if (typeof value === 'number') return String(value)
  return value.trim() ? value : emptyValue
}

function optionalTextValue(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed || trimmed === '[object Object]') return null
  return trimmed
}

function formatJobAssignment(job: Job): string {
  const service = job.service_type?.trim()
  const client = job.client?.name?.trim() || job.client_name?.trim()
  return [service, client].filter(Boolean).join(' — ') || '-'
}

function formatJobPlace(job: Job): string {
  const city = job.city?.trim()
  const postalCode = job.postal_code?.trim()
  const department =
    postalCode && postalCode.length >= 2 ? postalCode.slice(0, 2) : null
  return (
    [city, department ? `(${department})` : null].filter(Boolean).join(' ') ||
    job.address?.trim() ||
    '-'
  )
}

function formatJobHour(start?: string | null, end?: string | null): string {
  if (start && end) return `${start.slice(0, 5)} - ${end.slice(0, 5)}`
  if (start) return start.slice(0, 5)
  if (end) return end.slice(0, 5)
  return '-'
}

function toIsoDate(value: Date): string {
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function breakDurationLabel(start: string, end: string): string {
  if (!start || !end) return '--'
  const [startHours, startMinutes] = start.split(':').map(Number)
  const [endHours, endMinutes] = end.split(':').map(Number)
  if (
    [startHours, startMinutes, endHours, endMinutes].some(value =>
      Number.isNaN(value)
    )
  ) {
    return `${start} - ${end}`
  }

  const totalMinutes =
    endHours * 60 + endMinutes - (startHours * 60 + startMinutes)
  if (totalMinutes <= 0) return `${start} - ${end}`
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}` : `${hours}h`
  }
  return `${minutes} min`
}

// 2.10.5 / 2.10.6 — Technician detail container with summary + tabs (Profile /
// Performance / Documents). Figma 1140:41826.
export default function TechnicianDetailPage() {
  const { t } = useTranslation('technicians')
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { id } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const { data, isLoading, isError } = useTechnician(id)
  useBreadcrumbLabel(data?.data?.full_name)
  const [showDeactivate, setShowDeactivate] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const [showAvatarDialog, setShowAvatarDialog] = useState(false)
  const [avatarDraftFile, setAvatarDraftFile] = useState<File | null>(null)
  const [avatarMarkedForRemoval, setAvatarMarkedForRemoval] = useState(false)
  const [isSavingAvatar, setIsSavingAvatar] = useState(false)
  const [showLeavePanel, setShowLeavePanel] = useState(false)
  const [leaveFromDraft, setLeaveFromDraft] = useState('')
  const [leaveToDraft, setLeaveToDraft] = useState('')
  const [leaveErrors, setLeaveErrors] = useState<{
    from?: string
    to?: string
  }>({})
  const [isSavingLeave, setIsSavingLeave] = useState(false)
  const avatarInputRef = useRef<HTMLInputElement>(null)

  const requestedTab = searchParams.get('tab') ?? 'profile'
  const activeTab = (TAB_VALUES as readonly string[]).includes(requestedTab)
    ? requestedTab
    : 'profile'
  const setTab = (tab: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('tab', tab)
    setSearchParams(next, { replace: true })
  }

  const todayDate = new Date()
  todayDate.setHours(0, 0, 0, 0)
  const todayIsoDate = toIsoDate(todayDate)
  const weekStartDate = new Date(todayDate)
  weekStartDate.setDate(
    weekStartDate.getDate() - ((weekStartDate.getDay() + 6) % 7)
  )
  const weekEndDate = new Date(weekStartDate)
  weekEndDate.setDate(weekStartDate.getDate() + 6)
  const monthStartDate = new Date(todayDate)
  monthStartDate.setDate(1)
  const monthEndDate = new Date(
    monthStartDate.getFullYear(),
    monthStartDate.getMonth() + 1,
    0
  )

  useEffect(() => {
    if (!searchParams.has('tab') || requestedTab === activeTab) return

    const next = new URLSearchParams(searchParams)
    next.set('tab', activeTab)
    setSearchParams(next, { replace: true })
  }, [activeTab, requestedTab, searchParams, setSearchParams])

  const jobsParams = useListParams<{ date_from?: string; date_to?: string }>({
    date_from: todayIsoDate,
    date_to: todayIsoDate,
  })
  const historyJobsQuery = {
    ...jobsParams.query,
    sort_column: 'scheduled_date',
    sort_order: 'desc',
  } satisfies ListQuery
  const weekJobsQuery = {
    page: 1,
    limit: 100,
    date_from: toIsoDate(weekStartDate),
    date_to: toIsoDate(weekEndDate),
    sort_column: 'scheduled_date',
    sort_order: 'asc',
  } satisfies ListQuery
  const monthJobsQuery = {
    page: 1,
    limit: 100,
    date_from: toIsoDate(monthStartDate),
    date_to: toIsoDate(monthEndDate),
    sort_column: 'scheduled_date',
    sort_order: 'asc',
  } satisfies ListQuery
  const { data: jobsData, isLoading: jobsLoading } = useTechnicianJobs(
    id,
    historyJobsQuery,
    {
      enabled: activeTab === 'performance',
      staleTime: TECHNICIAN_DETAIL_TAB_STALE_TIME,
    }
  )
  const { data: weekJobsData, isLoading: weekJobsLoading } = useTechnicianJobs(
    id,
    weekJobsQuery,
    {
      enabled: activeTab === 'profile',
      staleTime: TECHNICIAN_DETAIL_TAB_STALE_TIME,
    }
  )
  const { data: monthJobsData } = useTechnicianJobs(id, monthJobsQuery, {
    enabled: activeTab === 'profile',
    staleTime: TECHNICIAN_DETAIL_TAB_STALE_TIME,
  })
  const preloadTab = (tab: TechnicianDetailTab) => {
    if (!id || tab === activeTab) return

    if (tab === 'profile') {
      void qc.prefetchQuery(
        technicianJobsQueryOptions(
          id,
          weekJobsQuery,
          TECHNICIAN_DETAIL_TAB_STALE_TIME
        )
      )
      void qc.prefetchQuery(
        technicianJobsQueryOptions(
          id,
          monthJobsQuery,
          TECHNICIAN_DETAIL_TAB_STALE_TIME
        )
      )
    } else if (tab === 'performance') {
      void qc.prefetchQuery(
        technicianJobsQueryOptions(
          id,
          historyJobsQuery,
          TECHNICIAN_DETAIL_TAB_STALE_TIME
        )
      )
    } else if (tab === 'documents') {
      void qc.prefetchQuery(
        technicianDocumentsQueryOptions(
          id,
          TECHNICIAN_DETAIL_DOCUMENTS_QUERY,
          TECHNICIAN_DETAIL_TAB_STALE_TIME
        )
      )
    }
  }
  const avatarPreviewUrl = useMemo(
    () => (avatarDraftFile ? URL.createObjectURL(avatarDraftFile) : null),
    [avatarDraftFile]
  )
  const activityRevenueRows = useMemo(() => {
    const totals = new Map<ActivityCategoryLabel, number>(
      activityCategoryConfigs.map(category => [category.label, 0])
    )

    for (const row of data?.data?.revenue_by_activity ?? []) {
      const activity = normalizeActivityCategory(row.activity)
      if (!activity || !Number.isFinite(row.amount)) continue
      totals.set(
        activity,
        (totals.get(activity) ?? 0) + Math.max(0, row.amount)
      )
    }

    const maxAmount = Math.max(
      0,
      ...activityCategoryConfigs.map(
        category => totals.get(category.label) ?? 0
      )
    )

    return activityCategoryConfigs
      .map((category, index) => {
        const amount = totals.get(category.label) ?? 0

        return {
          activity: category.label,
          amount,
          width: maxAmount > 0 ? `${(amount / maxAmount) * 100}%` : '0%',
          color: category.color,
          order: index,
        }
      })
      .sort((first, second) => {
        if (second.amount !== first.amount) return second.amount - first.amount
        return first.order - second.order
      })
  }, [data?.data?.revenue_by_activity])

  useEffect(() => {
    return () => {
      if (avatarPreviewUrl) URL.revokeObjectURL(avatarPreviewUrl)
    }
  }, [avatarPreviewUrl])

  if (isLoading) {
    return (
      <div className='space-y-4'>
        <Skeleton className='h-8 w-64' />
        <Skeleton className='h-96 w-full' />
      </div>
    )
  }

  if (isError || !data?.data) {
    return (
      <div className='space-y-4 py-10 text-center'>
        <p className='text-muted-foreground'>{t('detail.notFound')}</p>
        <Button onClick={() => navigate('/technicians')}>
          {t('detail.backToTechnicians')}
        </Button>
      </div>
    )
  }

  const tech = data.data
  const wh = (tech.working_hours ?? {}) as Record<string, unknown>
  const commissionRates = getCommissionRates(tech)
  const jobs = jobsData?.data ?? []
  const weekJobs = weekJobsData?.data ?? []
  const monthJobs = monthJobsData?.data ?? []
  const documentsCount = tech.documents_count
  const city = tech.city ?? (wh.city as string | undefined)
  const postalCode = tech.postal_code ?? (wh.postal_code as string | undefined)
  const address = tech.address ?? (wh.address as string | undefined)
  const certibiocideNo = optionalTextValue(tech.certibiocide_no)
  const worksWeekends =
    tech.works_weekends ??
    (typeof wh.weekends === 'boolean' ? wh.weekends : false)
  const equipment =
    tech.equipment ??
    (Array.isArray(wh.equipment)
      ? (wh.equipment as Array<string | { name?: string }>)
      : undefined)
  const vehicles =
    tech.vehicles ??
    (Array.isArray(wh.vehicles)
      ? (wh.vehicles as Array<string | { name?: string; plate?: string }>)
      : undefined)

  const zones = tech.zones ?? []
  const certifications = tech.certifications ?? []
  const breakRange = parseBreakRange(wh.break as string | undefined)
  const secondaryStatusDetail = getTechnicianSecondaryStatusDetail(tech, t)
  const equipmentItems = (equipment ?? []).map(itemLabel).filter(Boolean)
  const rolePreset = getTechnicianRolePreset(tech)
  const roleLabel = t(`form.rolePresets.${rolePreset}.title`)
  const secondaryPhone = formatPhoneNumber(tech.phone_secondary)
  const contactPhone = tech.phone?.trim() || tech.phone_secondary?.trim() || ''
  const displayPhone = formatPhoneNumber(contactPhone)
  const whatsappPhone = contactPhone.replace(/[^\d+]/g, '')
  const phoneSubtitle = contactPhone
    ? secondaryPhone
      ? `${t('detail.copyHint')} · ${t('detail.phoneSecondaryShort')} : ${secondaryPhone}`
      : t('detail.copyHint')
    : undefined
  const missionHistoryDate = jobsParams.filters.date_from ?? todayIsoDate
  const completedJobsLabel = t('detail.kpiMissions')
  const punctualityLabel = t('detail.kpiPunctuality')
  const revenueLabel = t('detail.kpiRevenue')
  const monthlyRevenue = formatCurrency(
    tech.revenue_generated ?? 0,
    'EUR',
    2,
    'suffix'
  )
  const monthlyCompletedJobs = tech.jobs_completed ?? 0
  const roleChipClass =
    rolePreset === 'commercial'
      ? 'bg-[#dbeafe] text-[#1d44b3]'
      : rolePreset === 'hybrid'
        ? 'bg-[#eafaf2] text-[#0e9f6e]'
        : 'bg-[#edf2ff] text-[#3658d8]'
  const workingDays =
    Array.isArray(wh.days) && wh.days.length
      ? fromApiWorkingDays(wh.days as string[])
      : DEFAULT_WORKING_DAYS
  const workingDaySet = new Set(workingDays.map(day => day.toLowerCase()))
  const jobsByDate = new Map<string, Job[]>()
  for (const job of weekJobs) {
    if (!job.scheduled_date) continue
    const key = job.scheduled_date
    const bucket = jobsByDate.get(key)
    if (bucket) {
      bucket.push(job)
    } else {
      jobsByDate.set(key, [job])
    }
  }
  const jobsByWeekday = new Map<number, Job[]>()
  for (const job of monthJobs) {
    if (!job.scheduled_date) continue
    const date = new Date(job.scheduled_date)
    if (Number.isNaN(date.getTime())) continue
    const weekdayIndex = (date.getDay() + 6) % 7
    const bucket = jobsByWeekday.get(weekdayIndex)
    if (bucket) {
      bucket.push(job)
    } else {
      jobsByWeekday.set(weekdayIndex, [job])
    }
  }
  const todayWeekdayIndex = (new Date().getDay() + 6) % 7
  const weekSchedule = TECHNICIAN_WEEK_DAYS_UI.map((day, index) => {
    const works = workingDaySet.has(day.toLowerCase())
    const isToday = index === todayWeekdayIndex
    const date = new Date(weekStartDate)
    date.setDate(weekStartDate.getDate() + index)
    const isoDate = toIsoDate(date)
    const weekDayJobs = [...(jobsByDate.get(isoDate) ?? [])].sort(
      (first, second) =>
        (first.start_time ?? '').localeCompare(second.start_time ?? '')
    )
    const monthWeekdayJobs = [...(jobsByWeekday.get(index) ?? [])].sort(
      (first, second) =>
        (first.start_time ?? '').localeCompare(second.start_time ?? '')
    )
    const displayJobs = weekDayJobs.length > 0 ? weekDayJobs : monthWeekdayJobs
    const label = t(`availability.days.${day.toLowerCase()}`, {
      defaultValue: day,
    })

    return {
      day,
      label,
      isToday,
      works,
      isoDate,
      jobs: displayJobs,
      time: works && wh.start && wh.end ? `${wh.start} - ${wh.end}` : null,
    }
  })
  const lunchBreakValue = breakDurationLabel(breakRange.start, breakRange.end)
  const vehicleCards = (vehicles ?? [])
    .map((vehicle, index) => ({
      key:
        typeof vehicle === 'string'
          ? `${vehicle}-${index}`
          : `${vehicle.name ?? 'vehicle'}-${vehicle.plate ?? index}`,
      name: itemName(vehicle) || itemLabel(vehicle),
      plate: typeof vehicle === 'string' ? null : (vehicle.plate ?? null),
    }))
    .filter(vehicle => vehicle.name)
  const copyToClipboard = async (value?: string | null) => {
    const text = value?.trim()
    if (!text) return
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
      } else {
        const input = document.createElement('textarea')
        input.value = text
        input.setAttribute('readonly', '')
        input.style.position = 'fixed'
        input.style.opacity = '0'
        document.body.appendChild(input)
        input.select()
        const copied = document.execCommand('copy')
        document.body.removeChild(input)
        if (!copied) throw new Error('Copy failed')
      }

      toast({ variant: 'success', title: t('detail.copySuccess') })
    } catch {
      toast({ variant: 'destructive', title: t('detail.copyError') })
    }
  }
  const onCall = () => {
    if (!contactPhone) return
    // eslint-disable-next-line react-hooks/immutability
    window.location.href = `tel:${contactPhone.replace(/\s+/g, '')}`
  }
  const onWhatsapp = () => {
    if (!whatsappPhone) return
    window.open(
      `https://wa.me/${whatsappPhone.replace(/^\+/, '')}`,
      '_blank',
      'noopener,noreferrer'
    )
  }
  const onEmail = () => {
    if (!tech.email?.trim()) return
    window.location.href = `mailto:${tech.email.trim()}`
  }

  const resetAvatarDialog = () => {
    setAvatarDraftFile(null)
    setAvatarMarkedForRemoval(false)
    if (avatarInputRef.current) avatarInputRef.current.value = ''
  }

  const onAvatarDialogChange = (open: boolean) => {
    setShowAvatarDialog(open)
    if (!open) resetAvatarDialog()
  }

  const onAvatarSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    const avatarError = validateSettingsImage(file)
    if (avatarError) {
      toast({
        variant: 'destructive',
        title:
          avatarError === 'type'
            ? t('detail.avatarUnsupportedFormat')
            : t('shared:attachFiles.tooLarge', {
                max: getFileSizeLimitMb(SETTINGS_IMAGE_MAX_BYTES),
              }),
      })
      return
    }
    setAvatarDraftFile(file)
    setAvatarMarkedForRemoval(false)
  }

  const onRemoveAvatar = () => {
    setAvatarDraftFile(null)
    setAvatarMarkedForRemoval(Boolean(tech.avatar_url))
  }

  const hasAvatarChanges = avatarMarkedForRemoval || !!avatarDraftFile
  const avatarDialogPreviewSrc = avatarMarkedForRemoval
    ? null
    : (avatarPreviewUrl ?? tech.avatar_url)

  const saveAvatar = async () => {
    if (!id || !hasAvatarChanges) {
      onAvatarDialogChange(false)
      return
    }

    setIsSavingAvatar(true)
    try {
      if (avatarMarkedForRemoval) {
        await techniciansApi.update(id, {
          ...technicianDetailToEditPayload(tech),
          avatar_file_id: null,
        })
      } else if (avatarDraftFile) {
        const uploadResponse = await techniciansApi.uploadDocument(
          id,
          avatarDraftFile,
          'avatar'
        )
        const fileId = uploadResponse.data?.file_id
        if (!fileId) throw new Error('Missing avatar file id')
        await techniciansApi.update(id, {
          ...technicianDetailToEditPayload(tech),
          avatar_file_id: fileId,
        })
      }

      await Promise.all([
        qc.invalidateQueries({ queryKey: ['technicians', 'detail', id] }),
        qc.invalidateQueries({ queryKey: ['technicians', id, 'documents'] }),
      ])

      toast({
        variant: 'success',
        title: avatarMarkedForRemoval
          ? t('detail.avatarRemoveSuccess')
          : t('detail.avatarUpdateSuccess'),
      })
      onAvatarDialogChange(false)
    } catch (error) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(error, t('detail.avatarUpdateError')),
      })
    } finally {
      setIsSavingAvatar(false)
    }
  }

  const openLeavePanel = () => {
    setLeaveFromDraft(tech.leave_from ?? '')
    setLeaveToDraft(tech.leave_to ?? '')
    setLeaveErrors({})
    setShowLeavePanel(true)
  }

  const onLeavePanelChange = (open: boolean) => {
    setShowLeavePanel(open)
    if (!open) setLeaveErrors({})
  }

  const validateLeaveDraft = () => {
    const next: { from?: string; to?: string } = {}
    if (!leaveFromDraft.trim()) {
      next.from = t('form.validation.leaveFrom')
    }
    if (!leaveToDraft.trim()) {
      next.to = t('form.validation.leaveTo')
    } else if (leaveFromDraft.trim() && leaveToDraft < leaveFromDraft) {
      next.to = t('form.validation.leaveToBeforeFrom')
    }
    setLeaveErrors(next)
    return next
  }

  const saveLeaveDates = async () => {
    const nextErrors = validateLeaveDraft()
    const firstError = nextErrors.from ?? nextErrors.to
    if (firstError) {
      toast({ variant: 'destructive', title: firstError })
      return
    }

    if (!id) return

    setIsSavingLeave(true)
    try {
      await techniciansApi.update(id, {
        ...technicianDetailToEditPayload(tech),
        on_leave: true,
        leave_from: leaveFromDraft,
        leave_to: leaveToDraft,
      })

      await Promise.all([
        qc.invalidateQueries({ queryKey: ['technicians', 'list'] }),
        qc.invalidateQueries({ queryKey: ['technicians', 'detail', id] }),
        qc.invalidateQueries({ queryKey: ['technicians', 'availability', id] }),
      ])

      toast({ variant: 'success', title: t('form.updateSuccess') })
      onLeavePanelChange(false)
    } catch (error) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(error, t('form.updateError')),
      })
    } finally {
      setIsSavingLeave(false)
    }
  }

  return (
    <div className='space-y-4'>
      <Tabs value={activeTab} onValueChange={setTab}>
        <div className='pb-4 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between'>
          <Button
            type='button'
            variant='outline'
            className='w-fit rounded-xl border-[#e7e9ef] bg-white px-4 py-2 text-sm font-medium leading-none text-[#171a20] shadow-none hover:bg-[#f8fafd]'
            onClick={() => navigate('/technicians')}
          >
            <ChevronLeft className='mr-2 size-4' />
            {t('detail.backToTechnicians')}
          </Button>

          <div className='flex flex-wrap items-center gap-3'>
            <Permit module='agenda' action='edit'>
              <Button
                type='button'
                className={primaryBtnClass}
                onClick={() => navigate(`/jobs/new?technician_id=${tech.id}`)}
              >
                <CalendarPlus className='mr-2 size-4' />
                {t('detail.assignIntervention')}
              </Button>
            </Permit>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type='button'
                  variant='outline'
                  className='h-10 w-10 rounded-xl border-[#e7e9ef] bg-white p-0 text-[#171a20] shadow-none hover:bg-[#f8fafd]'
                >
                  <MoreVertical className='size-4' />
                  <span className='sr-only'>{t('detail.moreActions')}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align='end'
                className='min-w-[220px] rounded-xl border-[#e7e9ef] p-1.5'
              >
                <Permit module='technicians' action='edit'>
                  <DropdownMenuItem
                    className='rounded-lg'
                    onClick={() => navigate(`/technicians/${tech.id}/edit`)}
                  >
                    <Pencil className='mr-2 size-4' />
                    {t('detail.edit')}
                  </DropdownMenuItem>
                </Permit>
                <Permit module='technicians' action='edit'>
                  <DropdownMenuItem
                    className='rounded-lg'
                    onClick={() => setShowDeactivate(true)}
                  >
                    <Power className='mr-2 size-4' />
                    {tech.status === 'active'
                      ? t('detail.deactivate')
                      : t('detail.activate')}
                  </DropdownMenuItem>
                </Permit>
                <Permit module='technicians' action='delete'>
                  <DropdownMenuItem
                    className='rounded-lg text-destructive focus:text-destructive'
                    onClick={() => setShowDelete(true)}
                  >
                    <Trash2 className='mr-2 size-4' />
                    {t('detail.delete')}
                  </DropdownMenuItem>
                </Permit>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className='grid gap-4 xl:grid-cols-[400px_minmax(0,1fr)] xl:items-start'>
          <aside>
            <section className={cn(figmaCardClass, 'rounded-2xl p-6')}>
              <div className='rounded-t-2xl border-b border-[#e7e9ef] bg-[linear-gradient(180deg,#fff3ec_0%,#fff8f4_35%,#fffcfa_70%,#fff_100%)] px-4 pb-6 pt-6 text-center'>
                <div className='relative mx-auto w-fit'>
                  <Avatar
                    name={tech.full_name}
                    src={tech.avatar_url}
                    className='mx-auto size-[72px] border-2 border-white font-heading text-2xl font-semibold text-white shadow-[0_1px_4px_-4px_rgba(38,45,66,0.05),0_8px_16px_-4px_rgba(38,45,66,0.1)]'
                    style={{ background: TECHNICIAN_AVATAR_BACKGROUND }}
                  />
                  <button
                    type='button'
                    onClick={() => setShowAvatarDialog(true)}
                    className='absolute bottom-0 right-0 flex size-7 translate-x-[2px] translate-y-[2px] items-center justify-center rounded-xl border-2 border-white bg-[#10b981] shadow-[0_8px_16px_-4px_rgba(38,45,66,0.1)] transition hover:bg-[#059669]'
                    aria-label={t('detail.editAvatar')}
                  >
                    <img src={editIcon} alt='' className='size-4' />
                  </button>
                </div>
                <h1 className='mt-4 font-heading text-[20px] font-bold leading-[1.2] text-[#171a20]'>
                  {tech.full_name}
                </h1>
                <div className='mt-4 flex flex-wrap justify-center gap-2'>
                  <TechnicianStatusBadge
                    status={getTechnicianPrimaryStatus(tech)}
                  />
                  <span
                    className={cn(
                      'inline-flex h-6 items-center gap-1 rounded-lg px-1.5 py-1 text-xs font-medium leading-4',
                      roleChipClass
                    )}
                  >
                    <img src={briefcaseIcon} alt='' className='size-[14px]' />
                    {roleLabel}
                  </span>
                </div>
                {secondaryStatusDetail ? (
                  <p className='mt-3 text-sm font-medium text-[#6c7285]'>
                    {secondaryStatusDetail}
                  </p>
                ) : null}
              </div>

              <Dialog
                open={showAvatarDialog}
                onOpenChange={onAvatarDialogChange}
              >
                <DialogContent className='max-w-[480px] gap-6 border-[#d9d9d9] p-8 shadow-lg sm:rounded-2xl [&>button]:right-2 [&>button]:top-2 [&>button]:rounded-full [&>button]:p-2'>
                  <DialogHeader>
                    <DialogTitle className='pr-8 text-2xl font-semibold leading-[1.2] tracking-[-0.48px]'>
                      {t('detail.editAvatar')}
                    </DialogTitle>
                    <p className='text-sm text-[#6c7285]'>
                      {t('detail.avatarDialogHint')}
                    </p>
                  </DialogHeader>

                  <div className='space-y-5'>
                    <div className='flex justify-center'>
                      <Avatar
                        name={tech.full_name}
                        src={avatarDialogPreviewSrc}
                        className='h-24 w-24 font-heading text-2xl font-semibold text-white'
                        style={{ background: TECHNICIAN_AVATAR_BACKGROUND }}
                      />
                    </div>

                    <input
                      ref={avatarInputRef}
                      type='file'
                      accept={SETTINGS_IMAGE_ACCEPT}
                      className='hidden'
                      onChange={onAvatarSelect}
                    />

                    <div className='flex flex-wrap justify-center gap-2'>
                      <Button
                        type='button'
                        variant='outline'
                        className='h-10 rounded-xl border-[#ff5a1f] px-3 text-sm font-medium leading-none text-[#ff5a1f] shadow-none hover:bg-[#fff4ef] hover:text-[#ff5a1f]'
                        onClick={() => avatarInputRef.current?.click()}
                      >
                        <Camera className='mr-2 size-4' />
                        {t('form.addPhoto')}
                      </Button>
                      {(tech.avatar_url || avatarDraftFile) &&
                      !avatarMarkedForRemoval ? (
                        <Button
                          type='button'
                          variant='outline'
                          className='h-10 rounded-xl border-[#b91c1c] px-3 text-sm font-medium leading-none text-[#991b1b] shadow-none hover:bg-[#fef2f2] hover:text-[#991b1b]'
                          onClick={onRemoveAvatar}
                        >
                          <Trash2 className='mr-2 size-4' />
                          {t('form.removeAvatar')}
                        </Button>
                      ) : null}
                    </div>
                  </div>

                  <DialogFooter className='gap-2 sm:justify-end sm:space-x-0'>
                    <Button
                      type='button'
                      variant='outline'
                      className='rounded-xl'
                      onClick={() => onAvatarDialogChange(false)}
                      disabled={isSavingAvatar}
                    >
                      {t('common:actions.cancel')}
                    </Button>
                    <Button
                      type='button'
                      className='rounded-xl'
                      onClick={saveAvatar}
                      disabled={!hasAvatarChanges || isSavingAvatar}
                    >
                      {t('common:actions.save')}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <div className='space-y-4 pt-4'>
                <SidebarMetaSection
                  title={t('detail.quickContact')}
                  titleClassName='uppercase'
                >
                  <div className='flex flex-wrap gap-2'>
                    <QuickActionButton
                      label={t('detail.callAction')}
                      iconSrc={phoneActionIcon}
                      tone='orange'
                      disabled={!contactPhone}
                      onClick={onCall}
                      className='flex-1 basis-[96px]'
                    />
                    <QuickActionButton
                      label={t('detail.whatsappAction')}
                      iconSrc={whatsappIcon}
                      tone='green'
                      disabled={!whatsappPhone}
                      onClick={onWhatsapp}
                      className='shrink-0'
                    />
                    <QuickActionButton
                      label={t('detail.emailAction')}
                      iconSrc={mailActionIcon}
                      tone='neutral'
                      disabled={!tech.email?.trim()}
                      onClick={onEmail}
                      className='flex-1 basis-[96px]'
                    />
                  </div>
                </SidebarMetaSection>

                <div className={detailDividerClass} />

                <SidebarMetaSection
                  title={t('detail.figuresOfMonth')}
                  titleClassName='uppercase'
                >
                  <div className='grid grid-cols-3 gap-2'>
                    <SummaryFigure
                      label={t('detail.sidebarKpiMissions')}
                      value={formatInteger(monthlyCompletedJobs)}
                      tone='blue'
                    />
                    <SummaryFigure
                      label={t('detail.sidebarKpiPunctuality')}
                      value={formatPercent(tech.punctuality_rate)}
                      tone='green'
                    />
                    <SummaryFigure
                      label={t('detail.sidebarKpiRevenue')}
                      value={monthlyRevenue}
                      tone='orange'
                    />
                  </div>
                  {!showLeavePanel ? (
                    <Button
                      type='button'
                      variant='outline'
                      className='h-10 w-full gap-2 rounded-xl border-[#e7e9ef] bg-white text-sm font-medium leading-none text-[#171a20] shadow-none hover:bg-[#f8fafd]'
                      onClick={openLeavePanel}
                    >
                      <img src={calendarIcon} alt='' className='size-4' />
                      {t('detail.takeLeave')}
                    </Button>
                  ) : null}
                  {showLeavePanel ? (
                    <div
                      id='technician-leave-inline-panel'
                      data-technician-leave-inline
                      className='space-y-4 pt-2'
                    >
                      <div className='space-y-2'>
                        <div className='space-y-1'>
                          <label
                            htmlFor='technician-leave-from'
                            className='text-xs font-medium leading-4 text-[#171a20]'
                          >
                            {t('form.from')}
                          </label>
                          <DatePicker
                            id='technician-leave-from'
                            value={leaveFromDraft}
                            onChange={value => {
                              setLeaveFromDraft(value)
                              setLeaveErrors(prev => ({
                                ...prev,
                                from: undefined,
                              }))
                            }}
                            max={leaveToDraft || undefined}
                            error={!!leaveErrors.from}
                            className={cn(
                              'rounded-xl border-0 bg-[#f4f5f8] px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] shadow-none [&_svg]:text-[#272c36] [&_input]:text-sm [&_input]:font-normal [&_input]:leading-[1.4] [&_input]:text-[#171a20]',
                              FORM_CONTROL_FOCUS_CLASS,
                              FORM_CONTROL_FOCUS_WITHIN_CLASS
                            )}
                          />
                          {leaveErrors.from ? (
                            <p className='text-xs text-destructive'>
                              {leaveErrors.from}
                            </p>
                          ) : null}
                        </div>

                        <div className='space-y-1'>
                          <label
                            htmlFor='technician-leave-to'
                            className='text-xs font-medium leading-4 text-[#171a20]'
                          >
                            {t('form.to')}
                          </label>
                          <DatePicker
                            id='technician-leave-to'
                            value={leaveToDraft}
                            onChange={value => {
                              setLeaveToDraft(value)
                              setLeaveErrors(prev => ({
                                ...prev,
                                to: undefined,
                              }))
                            }}
                            min={leaveFromDraft || undefined}
                            error={!!leaveErrors.to}
                            className={cn(
                              'rounded-xl border-0 bg-[#f4f5f8] px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] shadow-none [&_svg]:text-[#272c36] [&_input]:text-sm [&_input]:font-normal [&_input]:leading-[1.4] [&_input]:text-[#171a20]',
                              FORM_CONTROL_FOCUS_CLASS,
                              FORM_CONTROL_FOCUS_WITHIN_CLASS
                            )}
                          />
                          {leaveErrors.to ? (
                            <p className='text-xs text-destructive'>
                              {leaveErrors.to}
                            </p>
                          ) : null}
                        </div>
                      </div>

                      <div className='grid grid-cols-2 gap-4'>
                        <Button
                          type='button'
                          variant='outline'
                          className='h-10 rounded-xl border-[#e7e9ef] bg-white text-sm font-medium leading-none text-[#171a20] shadow-none hover:bg-[#f8fafd]'
                          onClick={() => onLeavePanelChange(false)}
                          disabled={isSavingLeave}
                        >
                          {t('common:actions.cancel')}
                        </Button>
                        <Button
                          type='button'
                          className='h-10 rounded-xl bg-primary text-sm font-medium leading-none text-white hover:bg-primary/90'
                          onClick={saveLeaveDates}
                          disabled={isSavingLeave}
                        >
                          <Check className='mr-2 size-4' />
                          {t('detail.leavePanelValidate')}
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </SidebarMetaSection>

                <div className={detailDividerClass} />

                <SidebarMetaSection
                  title={t('detail.contactDetails')}
                  titleClassName='uppercase'
                >
                  <div className='space-y-2'>
                    <div className='border-b border-dashed border-[#dfe4ee] pb-2'>
                      <ContactDetailItem
                        iconSrc={phoneDetailIcon}
                        title={displayValue(displayPhone)}
                        subtitle={phoneSubtitle}
                        onClick={
                          contactPhone
                            ? () => copyToClipboard(contactPhone)
                            : undefined
                        }
                      />
                    </div>
                    <div className='border-b border-dashed border-[#dfe4ee] pb-2'>
                      <ContactDetailItem
                        iconSrc={mailDetailIcon}
                        title={displayValue(tech.email)}
                        onClick={
                          tech.email?.trim()
                            ? () => copyToClipboard(tech.email)
                            : undefined
                        }
                      />
                    </div>
                    <div className='border-b border-dashed border-[#dfe4ee] pb-2'>
                      <ContactDetailItem
                        iconSrc={mapPinIcon}
                        title={displayValue(
                          [address, postalCode, city]
                            .filter(Boolean)
                            .join(', ') || null
                        )}
                      />
                    </div>
                    <div className='pb-2'>
                      <ContactDetailItem
                        iconSrc={certificateIcon}
                        iconClassName='size-4'
                        title={
                          certibiocideNo
                            ? t('detail.certibiocideInline', {
                                number: certibiocideNo,
                              })
                            : t('detail.certibiocide')
                        }
                        onClick={
                          certibiocideNo
                            ? () => copyToClipboard(certibiocideNo)
                            : undefined
                        }
                      />
                    </div>
                  </div>
                </SidebarMetaSection>
              </div>
            </section>
          </aside>

          <div className='space-y-4'>
            <div
              className={cn(
                figmaCardClass,
                'rounded-xl border-[#e7e9ef] p-2 shadow-[0_1px_4px_-4px_rgba(38,45,66,0.05),0_8px_16px_-4px_rgba(38,45,66,0.1)]'
              )}
            >
              <TabsList className='h-auto w-full flex-wrap justify-start gap-2 border-0 bg-transparent p-0 shadow-none'>
                {TAB_VALUES.map(value => (
                  <TabsTrigger
                    key={value}
                    value={value}
                    onPointerEnter={() => preloadTab(value)}
                    onFocus={() => preloadTab(value)}
                    className={tabTriggerClass}
                  >
                    {value === 'documents' ? (
                      <>
                        <span>{t('detail.tabs.file')}</span>
                        <span className='rounded-md bg-[#f4f5f8] px-1.5 py-0.5 text-xs font-medium leading-4 text-[#171a20] data-[state=active]:bg-white/16 data-[state=active]:text-white'>
                          {formatInteger(documentsCount)}
                        </span>
                      </>
                    ) : (
                      t(`detail.tabs.${value}`)
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            <TabsContent value='profile' className='mt-0 space-y-4'>
              <section className={figmaCardClass}>
                <FigmaCardHeader
                  title={t('detail.planningHolidays')}
                  icon={CalendarDays}
                  titleClassName='text-sm'
                />
                <div className='space-y-4 px-6 pb-6'>
                  <div className='grid gap-3 md:grid-cols-3 xl:grid-cols-7'>
                    {weekSchedule.map(item => (
                      <div
                        key={item.day}
                        className={cn(
                          'grid min-h-[120px] content-start auto-rows-min rounded-xl border px-3 py-3',
                          item.works
                            ? 'border-[#a7f3d0] bg-[#ecfdf5]'
                            : 'border-[#e7e9ef] bg-[#f4f5f8]',
                          item.isToday && 'shadow-[0_0_0_4px_#fff1ea]'
                        )}
                      >
                        <p className='h-5 text-sm font-medium leading-[1.4] text-[#171a20]'>
                          {item.label}
                        </p>
                        {item.isToday ? (
                          <p className='mt-1 h-4 text-xs font-medium uppercase leading-4 text-[#ff5a1f]'>
                            {t('detail.today')}
                          </p>
                        ) : null}
                        <p
                          className={cn(
                            'mt-1 min-h-8 text-xs font-medium leading-4',
                            item.works ? 'text-[#059669]' : 'text-[#6c7285]'
                          )}
                        >
                          {item.works
                            ? (item.time ?? t('detail.workingDay'))
                            : t('detail.restDay')}
                        </p>
                        {item.jobs.length > 0 ? (
                          <button
                            type='button'
                            className='mt-1 inline-flex items-center gap-1 self-start text-left text-xs font-medium leading-4 text-[#ff5a1f] transition hover:text-[#e14b12]'
                            onClick={() =>
                              navigate(`/calendar?job=${item.jobs[0].id}`)
                            }
                          >
                            <span>
                              {t('detail.missionCount', {
                                count: item.jobs.length,
                              })}
                            </span>
                            <ArrowRight className='size-4 shrink-0' />
                          </button>
                        ) : item.works ? (
                          weekJobsLoading ? (
                            <div className='mt-1 h-5 w-20 animate-pulse rounded bg-[#dff5ec]' />
                          ) : (
                            <button
                              type='button'
                              className='mt-1 inline-flex items-center gap-1 self-start text-left text-xs font-medium leading-4 text-[#ff5a1f] transition hover:text-[#e14b12]'
                              onClick={() =>
                                navigate(
                                  `/jobs/new?technician_id=${tech.id}&scheduled_date=${item.isoDate}`
                                )
                              }
                            >
                              <span>{t('detail.planMission')}</span>
                              <ArrowRight className='size-4 shrink-0' />
                            </button>
                          )
                        ) : null}
                      </div>
                    ))}
                  </div>

                  <div className='space-y-0 pt-1'>
                    <div className='flex items-center justify-between gap-4 border-b border-dashed border-[#dfe4ee] py-3'>
                      <span className='text-sm font-normal leading-[1.4] text-[#3d434e]'>
                        {t('detail.breakTime')}
                      </span>
                      <span className='text-sm font-medium leading-[1.4] text-[#171a20]'>
                        {lunchBreakValue}
                      </span>
                    </div>
                    <div className='flex items-center justify-between gap-4 border-b border-dashed border-[#dfe4ee] py-3'>
                      <span className='text-sm font-normal leading-[1.4] text-[#3d434e]'>
                        {t('detail.worksWeekends')}
                      </span>
                      <span className='text-sm font-medium leading-[1.4] text-[#171a20]'>
                        {worksWeekends ? t('detail.yes') : t('detail.no')}
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              <div className='grid gap-4 xl:grid-cols-2'>
                <section className={cn(figmaCardClass, 'xl:row-span-2')}>
                  <FigmaCardHeader
                    title={t('detail.skillsCommission')}
                    subtitle={t('detail.commissionSubtitle')}
                    icon={Shield}
                    tone='green'
                    titleClassName='text-sm'
                  />
                  <div className='space-y-1 px-6 pb-6'>
                    {(tech.skills ?? []).length === 0 ? (
                      <p className='text-sm text-muted-foreground'>
                        {t('detail.noSkills')}
                      </p>
                    ) : (
                      (tech.skills ?? []).map((skill, index) => {
                        const rate = commissionRates[skill]
                        const rateLabel =
                          rate == null
                            ? t('detail.commissionEmpty')
                            : typeof rate === 'number' ||
                                /^\d+(\.\d+)?$/.test(String(rate))
                              ? `${rate}%`
                              : String(rate)

                        return (
                          <div
                            key={skill}
                            className={cn(
                              'flex items-center justify-between gap-3 border-b border-dashed border-[#dfe4ee] py-3',
                              index === (tech.skills?.length ?? 0) - 1 &&
                                'border-b-0 pb-0'
                            )}
                          >
                            <div className='flex min-w-0 items-center gap-3'>
                              <span className='flex size-5 shrink-0 items-center justify-center text-[#98a2b3]'>
                                <Shield className='size-4' />
                              </span>
                              <span className='truncate text-sm font-medium leading-[1.4] text-[#171a20]'>
                                {skill}
                              </span>
                            </div>
                            <span className='shrink-0 rounded-md bg-[#eafaf2] px-2 py-1 text-xs font-medium leading-4 text-[#0e9f6e]'>
                              {rateLabel}
                            </span>
                          </div>
                        )
                      })
                    )}
                  </div>
                </section>

                <section className={figmaCardClass}>
                  <FigmaCardHeader
                    title={t('detail.certificationsSection')}
                    icon={FileBadge2}
                    tone='emerald'
                    titleClassName='text-sm'
                  />
                  <div className='space-y-3 px-6 pb-6'>
                    {certifications.length === 0 ? (
                      <p className='text-sm text-muted-foreground'>
                        {t('detail.noCertifications')}
                      </p>
                    ) : (
                      certifications.map((cert, index) => (
                        <div
                          key={`${cert.name}-${index}`}
                          className='rounded-xl border border-[#e7e9ef] bg-white px-4 py-3'
                        >
                          <p className='text-sm font-medium leading-[1.4] text-[#171a20]'>
                            {cert.name}
                          </p>
                          <p className='mt-1 text-xs font-normal leading-4 text-[#3d434e]'>
                            {cert.valid_until
                              ? t('detail.validUntil', {
                                  date: formatDate(cert.valid_until),
                                })
                              : t('detail.validUntilEmpty')}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </section>

                <section className={figmaCardClass}>
                  <FigmaCardHeader
                    title={t('detail.sector')}
                    icon={MapPin}
                    tone='green'
                    titleClassName='text-sm'
                  />
                  <div className='space-y-4 px-6 pb-6'>
                    <div className='flex flex-wrap gap-2'>
                      {zones.length > 0
                        ? zones.map(zone => <ZoneTag key={zone} label={zone} />)
                        : emptyValue}
                    </div>
                    {tech.latitude != null && tech.longitude != null ? (
                      <CoverageMap
                        center={{ lat: tech.latitude, lng: tech.longitude }}
                        radiusKm={tech.intervention_radius_km ?? 0}
                        height={260}
                        className='w-full'
                      />
                    ) : (
                      <div className='flex h-[260px] items-center justify-center rounded-2xl border border-dashed border-[#dfe4ee] bg-[#f8fafd] text-sm text-[#6c7285]'>
                        {t('detail.interventionZoneSection')}
                      </div>
                    )}
                  </div>
                </section>
              </div>

              <section className={figmaCardClass}>
                <FigmaCardHeader
                  title={t('detail.logistics')}
                  icon={Truck}
                  tone='amber'
                />
                <div className='space-y-5 px-6 pb-6'>
                  <div className='space-y-3'>
                    <div className='flex items-center gap-2'>
                      <span className='h-4 w-[3px] rounded-full bg-[#ff5a1f]' />
                      <p className={profileSectionLabelClass}>
                        {t('detail.vehiclesSection')}
                      </p>
                      {vehicleCards.length > 0 ? (
                        <span className='rounded-md bg-[#f4f5f8] px-2 py-1 text-xs font-medium leading-4 text-[#171a20]'>
                          {t('detail.itemsCount', {
                            count: vehicleCards.length,
                          })}
                        </span>
                      ) : null}
                    </div>
                    {vehicleCards.length > 0 ? (
                      <div className='space-y-3'>
                        {vehicleCards.map((vehicle, index) => (
                          <div
                            key={vehicle.key}
                            className='flex items-center justify-between gap-3 rounded-xl border border-[#e7e9ef] bg-white px-3 py-3'
                          >
                            <div className='flex min-w-0 items-center gap-3'>
                              <div className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-[#fbf3d9] text-[#a16207]'>
                                <Truck className='h-5 w-5' />
                              </div>
                              <div className='min-w-0'>
                                <p className='truncate text-sm font-medium leading-[1.4] text-[#171a20]'>
                                  {vehicle.name}
                                </p>
                                <p className='mt-0.5 text-xs font-normal leading-4 text-[#3d434e]'>
                                  {index === 0
                                    ? t('detail.mainVehicle')
                                    : t('detail.vehicleLabel', {
                                        index: index + 1,
                                      })}
                                </p>
                              </div>
                            </div>
                            {vehicle.plate ? (
                              <TechnicianReferenceBadge
                                value={vehicle.plate}
                                className='rounded-[12px] border-[#dfe3eb] bg-[#f5f6f9] shadow-[0_1px_2px_rgba(23,26,32,0.05)]'
                                labelClassName='px-4 py-[7px] text-sm font-medium leading-[1.4] text-[#171a20]'
                              />
                            ) : null}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className='rounded-xl border border-dashed border-[#dfe4ee] px-4 py-4 text-sm text-[#6c7285]'>
                        {emptyValue}
                      </div>
                    )}
                  </div>

                  <div className='space-y-3'>
                    <div className='flex items-center gap-2'>
                      <span className='h-4 w-[3px] rounded-full bg-[#ff5a1f]' />
                      <p className={profileSectionLabelClass}>
                        {t('detail.equipmentsSection')}
                      </p>
                      {equipmentItems.length > 0 ? (
                        <span className='rounded-md bg-[#f4f5f8] px-2 py-1 text-xs font-medium leading-4 text-[#171a20]'>
                          {t('detail.itemsCount', {
                            count: equipmentItems.length,
                          })}
                        </span>
                      ) : null}
                    </div>
                    {equipmentItems.length === 0 ? (
                      <div className='rounded-xl border border-dashed border-[#dfe4ee] px-4 py-4 text-sm text-[#6c7285]'>
                        {emptyValue}
                      </div>
                    ) : (
                      <div className='flex flex-wrap gap-2'>
                        {equipmentItems.map(label => (
                          <span
                            key={label}
                            className='rounded-md bg-[#f4f5f8] px-1.5 py-1 text-xs font-medium leading-4 text-[#171a20]'
                          >
                            {label}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </section>
            </TabsContent>

            <TabsContent value='performance' className='mt-0 space-y-4'>
              <section className={figmaCardClass}>
                <FigmaCardHeader
                  title={t('detail.performanceOfTheMonth')}
                  icon={PerformanceHeaderIcon}
                  titleClassName='text-sm'
                />
                <div className='space-y-6 px-6 pb-6 pt-4'>
                  <div className='grid gap-3 md:grid-cols-3'>
                    <PerformanceMetricCard
                      label={completedJobsLabel}
                      value={formatInteger(monthlyCompletedJobs)}
                      tone='blue'
                    />
                    <PerformanceMetricCard
                      label={punctualityLabel}
                      value={formatPercent(tech.punctuality_rate)}
                      tone='green'
                    />
                    <PerformanceMetricCard
                      label={revenueLabel}
                      value={monthlyRevenue}
                      tone='orange'
                    />
                  </div>

                  <div className={performanceDividerClass} />

                  <div className='space-y-5'>
                    <PerformanceSectionTitle
                      title={t('detail.revenueByActivity')}
                    />
                    {activityRevenueRows.map(row => (
                      <div
                        key={row.activity}
                        className='flex items-center gap-3 text-left'
                      >
                        <span className='min-w-0 flex-[0_0_240px] text-xs font-normal leading-4 text-[#171a20]'>
                          {row.activity}
                        </span>
                        <span className='h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-[#f4f5f8]'>
                          <span
                            className='block h-full rounded-full'
                            style={{
                              width: row.width,
                              backgroundColor: row.color,
                            }}
                          />
                        </span>
                        <span className='w-[100px] shrink-0 text-right text-xs font-medium leading-4 text-[#171a20] tabular-nums'>
                          {formatCurrency(row.amount, 'EUR', 2, 'suffix')}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className={performanceDividerClass} />

                  <div className='space-y-4'>
                    <div className='flex items-center justify-between gap-4 pb-1 pt-6'>
                      <PerformanceSubsectionHeader
                        title={t('detail.jobsHistory')}
                        icon={Clock3}
                      />
                      <div className='w-[170px] shrink-0'>
                        <DatePicker
                          value={missionHistoryDate}
                          onChange={value => {
                            const nextDate = value || todayIsoDate
                            jobsParams.setFilters({
                              date_from: nextDate,
                              date_to: nextDate,
                            })
                          }}
                          className={cn(
                            'h-10 rounded-xl border-[#e7e9ef] bg-[#F4F5F8] px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] shadow-none [&_svg]:text-[#272c36] [&_input]:text-sm [&_input]:font-normal [&_input]:leading-[1.4] [&_input]:text-[#171a20]',
                            FORM_CONTROL_FOCUS_CLASS,
                            FORM_CONTROL_FOCUS_WITHIN_CLASS
                          )}
                        />
                      </div>
                    </div>
                    <div className='overflow-x-auto'>
                      <Table className='caption-bottom text-sm'>
                        <TableHeader>
                          <TableRow className='border-0'>
                            <TableHead className={cn(thClass, 'text-left')}>
                              {t('detail.historyRef')}
                            </TableHead>
                            <TableHead className={cn(thClass, 'text-left')}>
                              {t('detail.historyAssignment')}
                            </TableHead>
                            <TableHead className={cn(thClass, 'text-left')}>
                              {t('detail.historyPlace')}
                            </TableHead>
                            <TableHead className={cn(thClass, 'text-left')}>
                              {t('detail.historyHour')}
                            </TableHead>
                            <TableHead className={cn(thClass, 'text-center')}>
                              {t('detail.status')}
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {jobsLoading &&
                            Array.from({ length: 4 }).map((_, index) => (
                              <TableRow key={index}>
                                <TableCell colSpan={5} className={tdClass}>
                                  <Skeleton className='h-6 w-full' />
                                </TableCell>
                              </TableRow>
                            ))}
                          {!jobsLoading && jobs.length === 0 && (
                            <TableRow>
                              <TableCell
                                colSpan={5}
                                className={cn(
                                  tdClass,
                                  'py-10 text-center text-muted-foreground'
                                )}
                              >
                                {t('detail.noJobHistory')}
                              </TableCell>
                            </TableRow>
                          )}
                          {!jobsLoading &&
                            jobs.map((job: Job) => (
                              <TableRow key={job.id}>
                                <TableCell
                                  className={cn(
                                    tdClass,
                                    'text-left font-medium hover:text-[#ff5a1f]'
                                  )}
                                >
                                  <button
                                    type='button'
                                    className='hover:underline'
                                    onClick={() =>
                                      navigate(`/calendar?job=${job.id}`)
                                    }
                                  >
                                    {job.reference ?? job.id.slice(0, 8)}
                                  </button>
                                </TableCell>
                                <TableCell
                                  className={cn(
                                    tdClass,
                                    'text-left font-normal text-[#171a20]'
                                  )}
                                >
                                  <div className='max-w-[204px] whitespace-normal'>
                                    {formatJobAssignment(job)}
                                  </div>
                                </TableCell>
                                <TableCell
                                  className={cn(
                                    tdClass,
                                    'text-left font-normal text-[#171a20]'
                                  )}
                                >
                                  {formatJobPlace(job)}
                                </TableCell>
                                <TableCell
                                  className={cn(
                                    tdClass,
                                    'text-left font-normal text-[#171a20]'
                                  )}
                                >
                                  {formatJobHour(job.start_time, job.end_time)}
                                </TableCell>
                                <TableCell
                                  className={cn(
                                    tdClass,
                                    'text-center text-[#171a20]'
                                  )}
                                >
                                  <JobStatusBadge
                                    status={job.status}
                                    label={t(`enums:jobStatus.${job.status}`, {
                                      defaultValue: job.status.replace(
                                        /_/g,
                                        ' '
                                      ),
                                    })}
                                    className='capitalize'
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                      <DataTablePagination
                        metadata={jobsData?.metadata}
                        page={jobsParams.page}
                        pageSize={jobsParams.limit}
                        onPageChange={jobsParams.setPage}
                        onPageSizeChange={jobsParams.setLimit}
                      />
                    </div>
                  </div>
                </div>
              </section>
            </TabsContent>

            <TabsContent value='documents' className='mt-0'>
              <div className='space-y-4'>
                {id && <DocumentsTab technicianId={id} />}
                {id && <NotesTab technicianId={id} notes={tech.notes} />}
              </div>
            </TabsContent>
          </div>
        </div>
      </Tabs>

      <DeactivateTechnicianDialog
        technician={showDeactivate ? tech : null}
        action={tech.status === 'active' ? 'deactivate' : 'activate'}
        futureJobsCount={tech.active_jobs_count}
        onClose={() => setShowDeactivate(false)}
        onDone={() => setShowDeactivate(false)}
      />
      <DeleteTechnicianDialog
        technician={showDelete ? tech : null}
        hasFutureJobs={tech.active_jobs_count > 0}
        onClose={() => setShowDelete(false)}
        redirectToList
        onDeactivateInstead={() => {
          setShowDelete(false)
          setShowDeactivate(true)
        }}
      />
    </div>
  )
}
